import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_THRESHOLD = 64;
const DEFAULT_MAX_DISTANCE = 104;
const DIRECTION_LOCK_DISTANCE = 8;
const PULL_RESISTANCE = 0.5;

interface UsePullToRefreshOptions {
	onRefresh: () => void | Promise<unknown>;
	disabled?: boolean;
	threshold?: number;
	maxDistance?: number;
}

const getPageScrollTop = () =>
	Math.max(window.scrollY, document.documentElement.scrollTop, document.body.scrollTop);

export function usePullToRefresh({
	onRefresh,
	disabled = false,
	threshold = DEFAULT_THRESHOLD,
	maxDistance = DEFAULT_MAX_DISTANCE,
}: UsePullToRefreshOptions) {
	const [container, setContainer] = useState<HTMLDivElement | null>(null);
	const containerRef = useCallback((node: HTMLDivElement | null) => setContainer(node), []);
	const onRefreshRef = useRef(onRefresh);
	const disabledRef = useRef(disabled);
	const isMountedRef = useRef(true);
	const isRefreshingRef = useRef(false);
	const pullDistanceRef = useRef(0);
	const [pullDistance, setPullDistance] = useState(0);
	const [isRefreshing, setIsRefreshing] = useState(false);

	useEffect(() => {
		onRefreshRef.current = onRefresh;
	}, [onRefresh]);

	useEffect(() => {
		disabledRef.current = disabled;
	}, [disabled]);

	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	useEffect(() => {
		if (!container) return;

		let isTracking = false;
		let direction: 'horizontal' | 'vertical' | null = null;
		let startX = 0;
		let startY = 0;

		const resetPull = () => {
			pullDistanceRef.current = 0;
			if (isMountedRef.current) setPullDistance(0);
		};

		const handleTouchStart = (event: TouchEvent) => {
			if (
				disabledRef.current ||
				isRefreshingRef.current ||
				event.touches.length !== 1 ||
				getPageScrollTop() > 0
			) {
				isTracking = false;
				return;
			}

			const touch = event.touches[0];
			startX = touch.clientX;
			startY = touch.clientY;
			direction = null;
			isTracking = true;
		};

		const handleTouchMove = (event: TouchEvent) => {
			if (!isTracking || event.touches.length !== 1) return;

			const touch = event.touches[0];
			const deltaX = touch.clientX - startX;
			const deltaY = touch.clientY - startY;

			if (!direction && Math.max(Math.abs(deltaX), Math.abs(deltaY)) >= DIRECTION_LOCK_DISTANCE) {
				direction = Math.abs(deltaY) > Math.abs(deltaX) ? 'vertical' : 'horizontal';
			}

			if (direction === 'horizontal' || deltaY <= 0 || getPageScrollTop() > 0) {
				isTracking = false;
				resetPull();
				return;
			}

			if (direction !== 'vertical') return;

			event.preventDefault();
			const nextDistance = Math.min(maxDistance, deltaY * PULL_RESISTANCE);
			pullDistanceRef.current = nextDistance;
			setPullDistance(nextDistance);
		};

		const handleTouchEnd = () => {
			if (!isTracking) return;
			isTracking = false;

			if (pullDistanceRef.current < threshold) {
				resetPull();
				return;
			}

			isRefreshingRef.current = true;
			setIsRefreshing(true);
			resetPull();

			Promise.resolve()
				.then(() => onRefreshRef.current())
				.catch(() => undefined)
				.finally(() => {
					isRefreshingRef.current = false;
					if (!isMountedRef.current) return;
					setIsRefreshing(false);
					resetPull();
				});
		};

		const handleTouchCancel = () => {
			isTracking = false;
			if (!isRefreshingRef.current) resetPull();
		};

		container.addEventListener('touchstart', handleTouchStart, { passive: true });
		container.addEventListener('touchmove', handleTouchMove, { passive: false });
		container.addEventListener('touchend', handleTouchEnd, { passive: true });
		container.addEventListener('touchcancel', handleTouchCancel, { passive: true });

		return () => {
			container.removeEventListener('touchstart', handleTouchStart);
			container.removeEventListener('touchmove', handleTouchMove);
			container.removeEventListener('touchend', handleTouchEnd);
			container.removeEventListener('touchcancel', handleTouchCancel);
		};
	}, [container, maxDistance, threshold]);

	return {
		containerRef,
		pullDistance,
		pullProgress: Math.min(pullDistance / threshold, 1),
		isPulling: pullDistance > 0 && !isRefreshing,
		isRefreshing,
	};
}
